import React, { useState } from 'react';
import { Card, CardContent, Typography, IconButton, Collapse, Stack } from '@mui/material';
import { HelpOutline as HelpIcon, Close as CloseIcon } from '@mui/icons-material';

interface HelpPanelProps {
  title: string;
  storageKey: string;
  children: React.ReactNode;
}

/**
 * A collapsible help panel that persists its dismissed state to localStorage.
 * Add to any page below the header to provide contextual guidance.
 */
export default function HelpPanel({ title, storageKey, children }: HelpPanelProps) {
  const lsKey = `cadence_help_dismissed_${storageKey}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(lsKey) === 'true');
  const [expanded, setExpanded] = useState(false);

  if (dismissed) return null;

  return (
    <Card
      sx={{
        mb: 2,
        bgcolor: 'rgba(108, 155, 255, 0.06)',
        border: '1px solid rgba(108, 155, 255, 0.15)',
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ cursor: 'pointer', flex: 1 }}
            onClick={() => setExpanded(!expanded)}
          >
            <HelpIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {title}
            </Typography>
          </Stack>
          <IconButton
            size="small"
            onClick={() => {
              setDismissed(true);
              localStorage.setItem(lsKey, 'true');
            }}
            sx={{ ml: 1 }}
            aria-label="Dismiss help"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
        <Collapse in={expanded}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {children}
          </Typography>
        </Collapse>
      </CardContent>
    </Card>
  );
}
